<template>
  <div
    v-if="visible && character"
    ref="menuElement"
    class="character-context-menu"
    :style="adjustedPositionStyle"
    @click.stop
  >
    <div class="context-menu-header">
      <div class="character-image-wrapper">
        <img
          v-if="character.image"
          :src="character.image"
          :alt="character.name"
          class="character-image"
          @error="onImageError"
        />
        <div v-else class="character-image-placeholder">
          <span>?</span>
        </div>
      </div>
      <div class="character-info">
        <h3 class="character-name">{{ character.name }}</h3>
        <p v-if="character.description" class="character-description">
          {{ character.description }}
        </p>
        <p v-else class="character-description no-description">
          No description available
        </p>
      </div>
    </div>
    <div v-if="showStats" class="context-menu-stats">
      <div class="stat-item">
        <span class="stat-label">Position:</span>
        <span class="stat-value">X: {{ character.x }}, Y: {{ character.y }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Layer:</span>
        <span class="stat-value">{{ character.layer }}</span>
      </div>
      <div v-if="character.currentActionPoints !== undefined" class="stat-item">
        <span class="stat-label">Action Points:</span>
        <span class="stat-value">{{ character.currentActionPoints }} / {{ character.maxActionPoints }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {fallbackOnError} from '@/utils/imageFallback';
import {avatarPlaceholderUrl} from '@/utils/avatarPlaceholder';

export default {
  name: 'CharacterContextMenu',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    character: {
      type: Object,
      default: null
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    showStats: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      menuWidth: 280,
      menuHeight: 200
    };
  },
  computed: {
    adjustedPositionStyle() {
      if (!this.visible || !this.character) {
        return { top: '0px', left: '0px' };
      }

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get menu dimensions (use actual if available, otherwise use estimates)
      let menuWidth = this.menuWidth;
      let menuHeight = this.menuHeight;

      if (this.$refs.menuElement) {
        menuWidth = this.$refs.menuElement.offsetWidth;
        menuHeight = this.$refs.menuElement.offsetHeight;
      }

      // Starting position from cursor
      let left = this.position.x;
      let top = this.position.y;

      // Add small offset from cursor for better UX
      const cursorOffset = 10;
      left += cursorOffset;
      top += cursorOffset;

      // Padding from viewport edges
      const edgePadding = 10;

      // Check right edge overflow
      if (left + menuWidth + edgePadding > viewportWidth) {
        // Flip to left of cursor
        left = this.position.x - menuWidth - cursorOffset;

        // If still overflows on left, clamp to edge
        if (left < edgePadding) {
          left = edgePadding;
        }
      }

      // Check bottom edge overflow
      if (top + menuHeight + edgePadding > viewportHeight) {
        // Flip above cursor
        top = this.position.y - menuHeight - cursorOffset;

        // If still overflows on top, clamp to edge
        if (top < edgePadding) {
          top = edgePadding;
        }
      }

      // Ensure menu doesn't overflow left edge
      if (left < edgePadding) {
        left = edgePadding;
      }

      // Ensure menu doesn't overflow top edge
      if (top < edgePadding) {
        top = edgePadding;
      }

      return {
        top: `${top}px`,
        left: `${left}px`
      };
    }
  },
  methods: {
    /* The menu is built from a board token — a name, a picture and a cell — with
       no biography behind it, so a broken picture falls back to `other.png`. */
    onImageError(event) {
      fallbackOnError(event, avatarPlaceholderUrl());
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Update menu dimensions after render
        this.$nextTick(() => {
          if (this.$refs.menuElement) {
            this.menuWidth = this.$refs.menuElement.offsetWidth;
            this.menuHeight = this.$refs.menuElement.offsetHeight;
            // Force re-computation of position
            this.$forceUpdate();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.character-context-menu {
  position: fixed;
  z-index: 10000;
  background: linear-gradient(135deg, rgba(20, 20, 30, 0.98), rgba(30, 30, 45, 0.98));
  border: 2px solid rgba(0, 212, 255, 0.6);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 212, 255, 0.3);
  min-width: 280px;
  max-width: 400px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.context-menu-header {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.character-image-wrapper {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(0, 212, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 212, 255, 0.4);
}

.character-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.character-image-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(100, 100, 120, 0.3), rgba(60, 60, 80, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: bold;
}

.character-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.character-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  line-height: 1.2;
}

.character-description {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
  max-height: 60px;
  overflow-y: auto;
}

.character-description.no-description {
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

.context-menu-stats {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.stat-label {
  color: rgba(0, 212, 255, 0.8);
  font-weight: 600;
}

.stat-value {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

/* Custom scrollbar for description */
.character-description::-webkit-scrollbar {
  width: 4px;
}

.character-description::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.character-description::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.4);
  border-radius: 2px;
}

.character-description::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.6);
}
</style>
